'use client';

/**
 * TenantFavicon Component
 *
 * Dynamically updates the favicon based on tenant branding. The tenant career
 * homepage may also apply its configured title and description; route-specific
 * metadata remains owned by Next.
 * Uses the favicon_url from tenant config if available,
 * otherwise falls back to the default VHire favicon.
 */

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useTenant } from '@/lib/tenant-context';

// Default VHire favicon path (SVG preferred for better scaling)
const DEFAULT_FAVICON = '/favicon.svg';

export function TenantFavicon() {
  const { tenant, isTenantDomain } = useTenant();
  const pathname = usePathname();

  useEffect(() => {
    // --- Favicon Update ---
    const faviconUrl = isTenantDomain && tenant?.branding?.favicon_url
      ? tenant.branding.favicon_url
      : DEFAULT_FAVICON;

    // Find existing favicon links
    const existingLinks = document.querySelectorAll<HTMLLinkElement>(
      'link[rel="icon"], link[rel="shortcut icon"]'
    );

    if (existingLinks.length > 0) {
      // Update existing favicon links
      existingLinks.forEach(link => {
        link.href = faviconUrl;
      });
    } else {
      // Create new favicon link if none exists
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconUrl;
      document.head.appendChild(link);
    }

    // Also update apple-touch-icon if tenant has one
    if (isTenantDomain && tenant?.branding?.favicon_url) {
      let appleIcon = document.querySelector<HTMLLinkElement>(
        'link[rel="apple-touch-icon"]'
      );

      if (!appleIcon) {
        appleIcon = document.createElement('link');
        appleIcon.rel = 'apple-touch-icon';
        document.head.appendChild(appleIcon);
      }

      appleIcon.href = tenant.branding.favicon_url;
    }

    // --- Tenant Page Title Update ---
    // Next metadata owns every route-specific title. Only the resolved tenant
    // career homepage may replace it here; writing a shared fallback after
    // hydration erases job, company, trust, editorial, and ATS titles.
    if (pathname === '/' && isTenantDomain && tenant) {
      const pageTitle = tenant.careerPage?.title
        || `Careers at ${tenant.company_name || tenant.name}`;
      document.title = pageTitle;
    }

    // --- Tenant Meta Description Update ---
    // As with the title, preserve Next's route metadata on non-home pages and
    // while tenant context is still loading.
    if (pathname === '/' && isTenantDomain && tenant) {
      let metaDescription = document.querySelector<HTMLMetaElement>(
        'meta[name="description"]'
      );

      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }

      // Use career page description if set, otherwise fallback to tenant description or tagline
      const description = tenant.careerPage?.description 
        || tenant.description 
        || tenant.branding?.tagline
        || `Explore career opportunities at ${tenant.company_name || tenant.name}`;
      metaDescription.content = description;
    }

    // --- Tenant Career Homepage Open Graph Tags ---
    if (pathname === '/' && isTenantDomain && tenant) {
      // OG Title
      let ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.content = tenant.careerPage?.title || `Careers at ${tenant.company_name || tenant.name}`;

      // OG Description
      let ogDescription = document.querySelector<HTMLMetaElement>('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.content = tenant.careerPage?.description 
        || tenant.description 
        || `Explore career opportunities at ${tenant.company_name || tenant.name}`;
    }
  }, [tenant, isTenantDomain, pathname]);

  // This component doesn't render anything
  return null;
}
